import type { TonyteActivity, TonyteUser } from '../types';

// Legacy detector — unchanged. Five different fields have accumulated over
// time to mark an event as StudiZone-only; kept exactly as-is as the
// fallback for events that predate the eventVisibility field below (see
// shouldShowInStudiZone) so no existing event's behavior changes.
export const isStudiZoneActivity = (activity: TonyteActivity) =>
  (activity as any).isStudentOnly === true ||
  (activity as any).layer === 'studizone' ||
  activity.visibility === 'student_only' ||
  (activity as any).audience === 'verified_students' ||
  (activity as any).zone === 'studyzone';

export type EventVisibility = 'main' | 'studizone' | 'both';

function getExplicitEventVisibility(activity: TonyteActivity): EventVisibility | null {
  const v = activity.eventVisibility;
  return v === 'main' || v === 'studizone' || v === 'both' ? v : null;
}

// Whether `activity` should appear in Main App surfaces (feed, map, and
// anything reading from the same shared cache, e.g. EventDetailScreen).
// Only University/Student Organization partners can ever write
// eventVisibility (enforced by firestore.rules), and only 'studizone'
// excludes an event from the Main App — 'main' and 'both' both show there,
// same as every event that has no eventVisibility field at all (every
// existing event, and every non-student-organization partner's events).
// This never newly excludes a legacy event — it only adds an exclusion for
// events that explicitly opted into StudiZone-only via the new field.
export function shouldShowInMainApp(activity: TonyteActivity): boolean {
  return getExplicitEventVisibility(activity) !== 'studizone';
}

// Whether `activity` should appear in StudiZone surfaces. Explicit
// eventVisibility is authoritative when present ('studizone' or 'both' ->
// included, 'main' -> excluded even if legacy markers are also somehow
// set). Falls back to the legacy isStudiZoneActivity check for events with
// no eventVisibility field — preserves useStudiZoneEvents' current
// inclusion criteria exactly for every event that predates this feature.
export function shouldShowInStudiZone(activity: TonyteActivity): boolean {
  const v = getExplicitEventVisibility(activity);
  if (v === 'studizone' || v === 'both') return true;
  if (v === 'main') return false;
  return isStudiZoneActivity(activity);
}

const normalizeDomain = (value: unknown) =>
  String(value || '').trim().toLowerCase().replace(/^@+/, '').replace(/\.+$/, '');

// University partners can restrict a StudiZone event to their own students
// ("My university only") via audienceDomains. A student matches when their
// verified student email domain equals one of the domains, or is a
// subdomain of one (so 'htw-dresden.de' also covers 'stud.htw-dresden.de').
// Admins/co-admins and the event's own host always see it. Events without
// audienceDomains are unaffected.
export function canProfileSeeStudiZoneActivity(
  activity: TonyteActivity,
  profile: Partial<Pick<TonyteUser, 'role' | 'studentEmailDomain'>> | null | undefined,
  viewerUid?: string | null,
): boolean {
  const domains = (activity.audienceDomains || []).map(normalizeDomain).filter(Boolean);
  if (!domains.length) return true;
  if (profile?.role === 'admin' || profile?.role === 'co-admin') return true;
  if (viewerUid && (activity.ownerUid === viewerUid || activity.hostId === viewerUid || activity.createdBy === viewerUid)) return true;
  const studentDomain = normalizeDomain(profile?.studentEmailDomain);
  if (!studentDomain) return false;
  return domains.some(domain => studentDomain === domain || studentDomain.endsWith(`.${domain}`));
}
